import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    Button,
    Fade,
    Grow
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Description as FileTextIcon,
    PictureAsPdf as FilePdfIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Add as AddIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const JDUploadMD3 = ({
                         onFilesAdded,
                         handleListEmpty,
                         manualEntryUsed = false,
                         onClearManualEntry,
                         disabled = false
                     }) => {
    const [fileList, setFileList] = useState([]);
    const fileListRef = useRef(fileList);

    // Keep ref in sync with state
    useEffect(() => {
        fileListRef.current = fileList;
        console.log('Current file list:', fileList);
    }, [fileList]);

    // Clear files when manual entry is used
    useEffect(() => {
        if (manualEntryUsed && fileList.length > 0) {
            console.log('Manual entry used, clearing uploaded files');
            setFileList([]);
            onFilesAdded([]);
            if (handleListEmpty) {
                handleListEmpty();
            }
        }
    }, [manualEntryUsed]);

    const onDrop = useCallback(acceptedFiles => {
        // Don't allow uploads if manual entry is used or component is disabled
        if (manualEntryUsed || disabled) {
            console.log('Upload disabled - manual entry used or component disabled');
            return;
        }

        // Only allow one file at a time
        if (fileListRef.current.length > 0) {
            console.log('Already have a file, not accepting more');
            return;
        }

        const filteredFiles = acceptedFiles.filter(file => {
            return file.type === 'application/pdf' ||
                file.type === 'application/msword' ||
                file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        });

        if (filteredFiles.length !== acceptedFiles.length) {
            console.error('Only Word and PDF files are allowed.');
        }

        if (filteredFiles.length > 0) {
            const newFile = {
                uid: filteredFiles[0].name + '_' + Date.now(),
                name: filteredFiles[0].name,
                status: 'done',
                file: filteredFiles[0],
                type: filteredFiles[0].type,
                size: filteredFiles[0].size
            };

            console.log('Adding file:', newFile.name);
            setFileList([newFile]);
            onFilesAdded([filteredFiles[0]]);

            // Clear manual entry if file is uploaded
            if (onClearManualEntry) {
                onClearManualEntry();
            }
        }
    }, [onFilesAdded, manualEntryUsed, disabled, onClearManualEntry]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
        },
        maxFiles: 1,
        disabled: fileList.length > 0 || manualEntryUsed || disabled,
        noClick: fileList.length > 0 || manualEntryUsed || disabled,
        noDrag: fileList.length > 0 || manualEntryUsed || disabled
    });

    const handleDelete = (fileToDelete) => {
        console.log('Deleting file:', fileToDelete.name);
        setFileList([]);
        onFilesAdded([]);
        if (handleListEmpty) {
            handleListEmpty();
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType === 'application/pdf') {
            return <FilePdfIcon sx={{ color: '#FF6B6B', fontSize: 24 }} />;
        }
        return <FileTextIcon sx={{ color: '#4DABF7', fontSize: 24 }} />;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    // Show disabled state when manual entry is used
    if (manualEntryUsed) {
        return (
            <Box sx={{ width: '100%' }}>
                <Box
                    sx={{
                        border: '2px dashed rgba(255, 255, 255, 0.1)',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.01)',
                        minHeight: 180,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.5,
                    }}
                >
                    <Box>
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                            }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 32, color: 'rgba(255, 255, 255, 0.3)' }} />
                        </Box>
                        <Typography
                            sx={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                mb: 0.5,
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }}
                        >
                            Upload Disabled
                        </Typography>
                        <Typography
                            sx={{
                                color: 'rgba(255, 255, 255, 0.4)',
                                fontSize: '0.875rem'
                            }}
                        >
                            Manual job description is active
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    // File list display when files are uploaded
    if (fileList.length > 0) {
        return (
            <Box sx={{ width: '100%' }}>
                <List sx={{ width: '100%', p: 0 }}>
                    {fileList.map((file) => (
                        <ListItem
                            key={file.uid}
                            sx={{
                                mb: 1.5,
                                p: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: 1.5,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <ListItemIcon>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        borderRadius: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {getFileIcon(file.type)}
                                </Box>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography
                                        sx={{
                                            color: '#FFFFFF',
                                            fontWeight: 500,
                                            fontSize: '1rem'
                                        }}
                                    >
                                        {file.name}
                                    </Typography>
                                }
                                secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
                                            {formatFileSize(file.size)}
                                        </Typography>
                                        <CheckCircleIcon sx={{ color: '#10B981', fontSize: 16 }} />
                                        <Typography sx={{ color: '#10B981', fontSize: '0.75rem' }}>
                                            Uploaded successfully
                                        </Typography>
                                    </Box>
                                }
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    onClick={() => handleDelete(file)}
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        '&:hover': {
                                            color: '#FF4444',
                                            backgroundColor: 'rgba(255, 68, 68, 0.1)',
                                        }
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </Box>
        );
    }

    // Upload zone when no files and manual entry not used
    return (
        <Box sx={{ width: '100%' }}>
            <Box
                {...getRootProps()}
                sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? '#FA370A' : 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    backgroundColor: isDragActive
                        ? 'rgba(250, 55, 10, 0.08)'
                        : 'rgba(255, 255, 255, 0.02)',
                    transition: 'all 0.2s ease',
                    minHeight: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: disabled ? 0.5 : 1,
                    '&:hover': !disabled ? {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    } : {}
                }}
            >
                <input {...getInputProps()} />
                <Box>
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            backgroundColor: disabled ? 'rgba(255, 255, 255, 0.1)' : '#FA370A',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                        }}
                    >
                        <CloudUploadIcon sx={{
                            fontSize: 32,
                            color: disabled ? 'rgba(255, 255, 255, 0.3)' : 'white'
                        }} />
                    </Box>

                    <Typography
                        sx={{
                            color: disabled ? 'rgba(255, 255, 255, 0.5)' : '#FFFFFF',
                            mb: 0.5,
                            fontWeight: 500,
                            fontSize: '1.1rem',
                        }}
                    >
                        {isDragActive ? 'Drop your file here' : 'Upload Job Description'}
                    </Typography>
                    <Typography
                        sx={{
                            color: disabled ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)',
                            mb: 2,
                            fontSize: '0.875rem'
                        }}
                    >
                        Drag and drop or{' '}
                        <Box
                            component="span"
                            sx={{
                                color: disabled ? 'rgba(255, 255, 255, 0.4)' : '#FA370A',
                                fontWeight: 500,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                '&:hover': !disabled ? {
                                    textDecoration: 'underline',
                                } : {}
                            }}
                        >
                            browse files
                        </Box>
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Chip
                            label="PDF"
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: disabled ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.75rem',
                            }}
                        />
                        <Chip
                            label="DOC"
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: disabled ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.75rem',
                            }}
                        />
                        <Chip
                            label="DOCX"
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: disabled ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.75rem',
                            }}
                        />
                        <Typography sx={{
                            color: disabled ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.75rem'
                        }}>
                            • Max 50MB
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default JDUploadMD3;